import React, { useState } from 'react';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { MessageCircle, Send, Copy, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { NOTIFY_TEMPLATES } from '../constants';
import { prepareMessage, buildWhatsAppLink, confirmWhatsAppSent, sendSmsUpdate, copySmsText } from '../services/complaintNotificationService';

const TEMPLATE_KEYS = Object.keys(NOTIFY_TEMPLATES);

const SendCustomerUpdateModal = ({ isOpen, onClose, ticket, currentUser }) => {
    const [channel, setChannel] = useState('whatsapp');
    const [templateKey, setTemplateKey] = useState('registered');
    const [busy, setBusy] = useState(false);
    const [waOpened, setWaOpened] = useState(false);
    const [result, setResult] = useState(null); // {status, errorMessage}

    const preview = prepareMessage(ticket, templateKey);

    const doSendSms = async () => {
        if (!window.confirm(`Send this SMS to ${ticket.mobile}?\n\n${preview}`)) return;
        setBusy(true);
        try {
            const r = await sendSmsUpdate(ticket, templateKey, currentUser);
            setResult(r);
            if (r.status === 'sent') setTimeout(onClose, 900);
        } finally { setBusy(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Send Customer Update" className="max-w-lg">
            <div className="space-y-3">
                <div className="flex gap-2">
                    {['whatsapp', 'sms'].map(c => (
                        <button key={c} onClick={() => { setChannel(c); setResult(null); }}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium border ${channel === c ? 'bg-purple-600 text-white border-purple-600' : 'border-slate-200 hover:bg-slate-50'}`}>
                            {c === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                        </button>
                    ))}
                </div>

                <label className="text-xs font-semibold text-slate-500 block">Template
                    <select className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-lg text-sm" value={templateKey}
                        onChange={e => { setTemplateKey(e.target.value); setResult(null); setWaOpened(false); }}>
                        {TEMPLATE_KEYS.map(k => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
                    </select>
                </label>

                <div>
                    <div className="text-xs font-semibold text-slate-500 mb-1">Preview</div>
                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">{preview}</div>
                    <div className="text-xs text-slate-400 mt-1">To: {ticket.mobile || '—'}</div>
                </div>

                {result && (
                    <div className={`flex items-start gap-2 text-sm rounded-lg p-2 ${result.status === 'sent' ? 'bg-green-50 text-green-700' : result.status === 'not_configured' || result.status === 'skipped' || result.status === 'disabled' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
                        {result.status === 'sent' ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                        <span>
                            {result.status === 'sent' && 'SMS sent successfully.'}
                            {result.status === 'not_configured' && 'SMS gateway not configured.'}
                            {result.status === 'skipped' && 'Duplicate — an identical SMS was sent in the last 5 minutes.'}
                            {result.status === 'disabled' && 'This event is disabled in settings.'}
                            {result.status === 'failed' && `Failed: ${result.errorMessage || 'unknown error'}`}
                        </span>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    {channel === 'whatsapp' ? (
                        <>
                            <a href={buildWhatsAppLink(ticket, templateKey)} target="_blank" rel="noreferrer" onClick={() => setWaOpened(true)}>
                                <Button variant="outline" size="sm"><ExternalLink size={15} /> Open WhatsApp</Button>
                            </a>
                            <Button size="sm" disabled={busy || !waOpened}
                                onClick={async () => { setBusy(true); try { await confirmWhatsAppSent(ticket, templateKey, currentUser); onClose(); } finally { setBusy(false); } }}>
                                <MessageCircle size={15} /> Mark as Sent
                            </Button>
                        </>
                    ) : (
                        <>
                            {(result?.status === 'not_configured') && (
                                <Button variant="outline" size="sm" onClick={() => copySmsText(ticket, templateKey)}><Copy size={15} /> Copy SMS Text</Button>
                            )}
                            <Button size="sm" disabled={busy} onClick={doSendSms}><Send size={15} /> Send SMS</Button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default SendCustomerUpdateModal;
